import { forwardRef, memo, Ref, useCallback } from 'react';
import DentistToolsIcon from '~/images/dentist-tools.svg';
import { Colors } from '~/themes/colors';
import { Value } from './Field';
import { Radio } from './Radio';
import { IInputJobProps, TOptionValue } from './Types';

const options: TOptionValue<boolean>[] = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const Component = (
  {
    defaultValue,
    isDisabled = false,
    isOpen = false,
    value: controlledValue,
    onChange,
    onToggle,
  }: IInputJobProps<boolean>,
  ref: Ref<HTMLDivElement>,
) => {
  const CustomValueComponent = useCallback(
    (currentValue: boolean | undefined) => {
      let displayText = 'Select';

      if (!isOpen) {
        displayText = currentValue ? 'Yes' : 'No';
      }

      return (
        <Value
          text={displayText}
          style={{
            color: isOpen || currentValue === undefined ? Colors.neutral_300 : Colors.neutral_600,
          }}
        />
      );
    },
    [isOpen],
  );

  return (
    <Radio
      IconSrc={DentistToolsIcon}
      ValueComponent={CustomValueComponent}
      defaultValue={defaultValue}
      isDisabled={isDisabled}
      isOpen={isOpen}
      value={controlledValue}
      onChange={onChange}
      onToggle={onToggle}
      options={options}
      title="Assisted Hygiene Available:"
      testName="input-job.assisted-hygiene"
      ref={ref}
    />
  );
};

/**
 * Assisted Hygiene Available selection component for job input forms.
 *
 * A specialized radio field component that allows users to indicate whether
 * assisted hygiene is available. Built on top of the Radio component with
 * boolean value handling and yes/no options.
 *
 * Features:
 * - Simple yes/no selection
 * - User icon for visual identification
 * - Boolean API (accepts and returns boolean values)
 * - Inherits all Radio component functionality (collapsible, controlled/uncontrolled)
 *
 * @example
 * ```tsx
 * <AssistedHygiene
 *   value={true}
 *   onChange={(isAvailable) => setAssistedHygiene(isAvailable)}
 * />
 * ```
 */
export const AssistedHygiene = memo(forwardRef(Component)) as <T = HTMLDivElement>(
  props: IInputJobProps<boolean> & { ref?: Ref<T> },
) => ReturnType<typeof Component>;
